import React, { memo } from 'react';
import cn from 'classnames';

import { Image } from 'components';

import { Avatar } from 'components/Avatar/Avatar.component';
import styles from './ResponsiveCard.module.scss';

export interface ResponsiveCardProps {
  /** Custom class name */
  className?: string | null;
  /** Custom style */
  style?: Record<string, unknown>;
  /** Card Heading */
  title: string;
  /** Primary image */
  image: string;
  /** Card Description */
  description: string;
  /** Secondary image */
  descriptionIcon?: string;
  /** To specify if the image should be rounded */
  isRounded?: boolean;
  /** onClick handler*/
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /** load image image lazily? */
  isLazy?: boolean;
}

export const ResponsiveCard: React.FC<ResponsiveCardProps> = memo(
  ({
    className = null, // custom class name
    style, // custom style
    title,
    image,
    description,
    descriptionIcon,
    isRounded = false,
    isLazy = true,
    onClick,
  }: ResponsiveCardProps) => {
    const contentClassNames = cn(
      {
        [styles.container]: true,
      },
      className
    );

    const imgContentClass = cn(
      {
        [styles.image]: true,
        [styles.imageRounded]: isRounded,
      },
      className
    );
    const initials = title
      ?.split(' ')
      .map((word) => word[0])
      .join('');

    return (
      <section
        className={contentClassNames}
        style={style}
        data-testid="ResponsiveCard"
        onClick={onClick}
      >
        <div className={styles.imageContainer}>
          <Avatar
            icon={image}
            name={initials}
            className={initials ? '' : imgContentClass}
            altText={`Logo of ${title}`}
            size={'large'}
          />
        </div>
        <section className={styles.content}>
          <header>
            <h3 className={styles.title}>{title}</h3>
          </header>
          <footer className={styles.footer}>
            {descriptionIcon && (
              <Image
                className={styles.descriptionIcon}
                src={descriptionIcon}
                alt={description}
                loading={isLazy ? 'lazy' : undefined}
              />
            )}
            <p className={styles.description}>{description}</p>
          </footer>
        </section>
      </section>
    );
  }
);

ResponsiveCard.displayName = 'ResponsiveCard';
